import React, { useState, useEffect, useCallback } from 'react';
import { T, FONTS } from '../../constants/theme.js';
import { fetchStaff, createStaff } from '../../services/api.js';
import { Card, Btn, Modal, Field, Input, Select, Badge, Avatar, Table, TR, TD, SectionHeader, SearchInput, Empty, Toast, Spinner, Tabs, OfflineBadge, useOfflineInvalidation } from '../../components/ui.jsx';

const ROLES = [
  { id:'ADMIN',          label:'Admin',            desc:'Full access to all modules and settings' },
  { id:'MANAGER',        label:'Manager',          desc:'Manage reservations, staff, reports' },
  { id:'FRONT_DESK',     label:'Front Desk',       desc:'Check-in/out, reservations, folio' },
  { id:'HOUSEKEEPER',    label:'Housekeeper',      desc:'Housekeeping tasks and room status' },
  { id:'ACCOUNTANT',     label:'Accountant',       desc:'Folio, billing, financial reports' },
  { id:'RESERVATIONIST', label:'Reservationist',   desc:'Manage reservations and availability' },
  { id:'READONLY',       label:'Read-only',        desc:'View-only access' },
];

const ROLE_COLOR = {
  ADMIN:'#6B46C1', MANAGER:T.teal, FRONT_DESK:T.success,
  HOUSEKEEPER:T.warning, ACCOUNTANT:T.gold, RESERVATIONIST:T.navy, READONLY:T.muted,
};

const SHIFTS = ['morning','afternoon','evening','night','flexible'];

const emptyStaff = {
  firstName:'', lastName:'', email:'', phone:'',
  role:'FRONT_DESK', shift:'morning', pin:'', notes:'',
};

const StaffForm = ({ data, onChange }) => (
  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
    <Field label="First Name" required><Input value={data.firstName} onChange={v => onChange('firstName', v)} /></Field>
    <Field label="Last Name"  required><Input value={data.lastName}  onChange={v => onChange('lastName', v)} /></Field>
    <Field label="Email"      required hint="Login credentials sent here">
      <Input type="email" value={data.email} onChange={v => onChange('email', v)} />
    </Field>
    <Field label="Phone"><Input value={data.phone} onChange={v => onChange('phone', v)} /></Field>
    <Field label="Role" required>
      <Select value={data.role} onChange={v => onChange('role', v)}>
        {ROLES.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
      </Select>
    </Field>
    <Field label="Shift">
      <Select value={data.shift} onChange={v => onChange('shift', v)}>
        {SHIFTS.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
      </Select>
    </Field>
    <Field label="PIN (4-digit, optional)" hint="Quick login at front desk">
      <Input type="password" value={data.pin} onChange={v => onChange('pin', v)} placeholder="••••" maxLength={4} />
    </Field>
    <Field label="Department / Notes">
      <Input value={data.notes} onChange={v => onChange('notes', v)} placeholder="e.g. Day shift leader" />
    </Field>
  </div>
);

const ACTION_ICONS = {
  CHECK_IN:'✅', CHECK_OUT:'🚪', CANCEL_RESERVATION:'❌', CREATE_STAFF:'👤',
  UPDATE_STAFF:'✏️', ACTIVATE_STAFF:'🟢', DEACTIVATE_STAFF:'🔴',
  CREATE_RESERVATION:'📋', UPDATE_ROOM_STATUS:'🏨', DEFAULT:'📝',
};

export default function StaffManagement({ property, onNavigate }) {
  const [staff,    setStaff]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState('');
  const [tab,      setTab]      = useState('active');
  const [toast,    setToast]    = useState('');
  const [saving,   setSaving]   = useState(false); // used by create form

  // Modals
  const [createOpen, setCreateOpen] = useState(false);
  const [rolesGuide, setRolesGuide] = useState(false);

  const [form, setForm] = useState({ ...emptyStaff });

  const pid = property?.id;
  const showToast = msg => { setToast(msg); setTimeout(() => setToast(''), 3500); };

  const load = useCallback(() => {
    if (!pid) return;
    setLoading(true);
    fetchStaff(pid)
      .then(data => setStaff(Array.isArray(data) ? data : []))
      .catch(() => setStaff([]))
      .finally(() => setLoading(false));
  }, [pid]);

  useOfflineInvalidation('staff', load);

  useEffect(() => { load(); }, [load]);

  const filtered = staff.filter(s => {
    const active = tab === 'active' ? s.active !== false : s.active === false;
    const q = search.toLowerCase();
    const name = `${s.firstName} ${s.lastName}`.toLowerCase();
    return active && (!q || name.includes(q) || s.email?.toLowerCase().includes(q) || s.role?.toLowerCase().includes(q));
  });

  const handleCreate = async () => {
    if (!form.firstName || !form.email || !form.role) { showToast('Name, email and role required'); return; }
    setSaving(true);
    try {
      const r = await createStaff({ ...form, propertyId: pid, fullName: `${form.firstName} ${form.lastName}` });
      showToast(r?._offline ? '⏳ Staff saved offline — will sync when server is back' : '✅ Staff member created — credentials sent by email');
      setCreateOpen(false);
      setForm({ ...emptyStaff });
      load();
    } catch (e) { showToast(`❌ ${e.message}`); }
    finally { setSaving(false); }
  };


  const TABS = [
    { id:'active',   label:'Active Staff',    count: staff.filter(s => s.active !== false).length },
    { id:'inactive', label:'Inactive',        count: staff.filter(s => s.active === false).length },
  ];

  return (
    <div className="fade-up">
      <SectionHeader
        title="Staff & Users"
        subtitle={`${staff.filter(s => s.active !== false).length} active staff members`}
        right={<>
          <Btn variant="secondary" size="sm" onClick={() => setRolesGuide(true)}>📋 Roles Guide</Btn>
          <Btn variant="primary" onClick={() => { setForm({...emptyStaff}); setCreateOpen(true); }}>+ Add Staff</Btn>
        </>}
      />

      <div style={{ display:'flex', gap:12, marginBottom:16, alignItems:'center' }}>
        <div style={{ flex:1, maxWidth:380 }}>
          <SearchInput value={search} onChange={setSearch} placeholder="Search by name, email, role..." />
        </div>
      </div>

      <Tabs tabs={TABS} active={tab} onSelect={setTab} />

      <Card style={{ padding:0, overflow:'hidden' }}>
        <div className="r-table-wrap">
        <Table headers={['Staff Member','Role','Shift','Email / Phone','Status','']}>
          {loading ? (
            <tr><td colSpan={6} style={{ textAlign:'center', padding:40 }}><Spinner /></td></tr>
          ) : filtered.length === 0 ? (
            <tr><td colSpan={6}><Empty icon="👤" title="No staff found" /></td></tr>
          ) : filtered.map(s => (
            <TR key={s.id}>
              <TD>
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <Avatar name={s.fullName || `${s.firstName} ${s.lastName}`} size={34} color={ROLE_COLOR[s.role] || T.teal} />
                  <div>
                    <p style={{ fontSize:13, fontWeight:700, color:T.navy }}>{s.fullName || `${s.firstName} ${s.lastName}`}</p>
                    {s.notes && <p style={{ fontSize:10, color:T.muted }}>{s.notes}</p>}
                  </div>
                </div>
              </TD>
              <TD>
                <Badge label={ROLES.find(r => r.id === s.role)?.label || s.role} color={ROLE_COLOR[s.role] || T.muted} />
              </TD>
              <TD><span style={{ fontSize:12, color:T.slate, textTransform:'capitalize' }}>{s.shift || '—'}</span></TD>
              <TD>
                <div style={{ fontSize:11, color:T.muted }}>
                  <div>{s.email}</div>
                  <div>{s.phone || '—'}</div>
                </div>
              </TD>
              <TD>
                <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                  <div style={{ width:7, height:7, borderRadius:'50%', background: s.active !== false ? T.success : T.muted }} />
                  <span style={{ fontSize:11, color: s.active !== false ? T.success : T.muted }}>
                    {s.active !== false ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </TD>
              <TD>
                <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                  <Btn size="sm" variant="primary"
                    onClick={() => onNavigate && onNavigate('staff-detail', s.id)}>
                    👤 Detail
                  </Btn>
                  <Btn size="sm" variant="secondary"
                    onClick={() => onNavigate && onNavigate('staff-logs', s.id)}>
                    📋 Logs
                  </Btn>
                </div>
              </TD>
            </TR>
          ))}
        </Table>
        </div>
      </Card>

      {/* Create Staff */}
      <Modal open={createOpen} onClose={() => setCreateOpen(false)} title="+ Add Staff Member" width={620}>
        <StaffForm data={form} onChange={(k, v) => setForm(p => ({...p, [k]:v}))} />
        <div style={{ marginTop:12, padding:'10px 14px', background:T.tealPale, borderRadius:10 }}>
          <p style={{ fontSize:12, color:T.teal }}>ℹ️ A welcome email with login credentials will be sent to the staff member's email address. They must be associated with tenant <strong>{property?.name}</strong>.</p>
        </div>
        <div style={{ display:'flex', gap:10, marginTop:16 }}>
          <Btn variant="secondary" onClick={() => setCreateOpen(false)} style={{ flex:1 }}>Cancel</Btn>
          <Btn variant="primary" disabled={saving} onClick={handleCreate} style={{ flex:2 }}>{saving ? 'Creating…' : 'Create Staff Member'}</Btn>
        </div>
      </Modal>

      {/* Roles Guide */}
      <Modal open={rolesGuide} onClose={() => setRolesGuide(false)} title="📋 Roles & Permissions Guide" width={560}>
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {ROLES.map(r => (
            <div key={r.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 14px', background:T.surface, borderRadius:10, border:`1px solid ${T.border}` }}>
              <div style={{ width:10, height:10, borderRadius:'50%', background:ROLE_COLOR[r.id] || T.muted, flexShrink:0 }} />
              <div>
                <p style={{ fontSize:13, fontWeight:700, color:T.navy }}>{r.label}</p>
                <p style={{ fontSize:12, color:T.muted }}>{r.desc}</p>
              </div>
            </div>
          ))}
        </div>
        <div style={{ marginTop:16 }}>
          <Btn variant="secondary" style={{ width:'100%' }} onClick={() => setRolesGuide(false)}>Close</Btn>
        </div>
      </Modal>

      <Toast message={toast} />
    </div>
  );
}
